import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import type { UserPresence } from "~/lib/prose/plugins/multicursor";

interface AttendeeSession {
	sessionId: string;
	isCurrentlyViewing: boolean;
	lastActive: number;
}

interface AttendeesPresenceInfo {
	// The key is the user's id as sent by ProseMultiCursorHandler
	[userId: string]: AttendeeSession[];
}

type AnonymousAttendee = Pick<UserPresence["user"], "id" | "fullName" | "email" | "image">;

export const AttendeePresenceContext = createContext<{
	attendeesPresenceInfo: AttendeesPresenceInfo;
	anonymousAttendees: AnonymousAttendee[];
	onUserPresenceChange: (presence: UserPresence, addUserToAnonymous: boolean) => void;
}>({
	attendeesPresenceInfo: {},
	anonymousAttendees: [],
	onUserPresenceChange: () => {
		throw new Error("AttendeePresenceContext is missing!");
	},
});

export default function AttendeePresenceContextProvider({ children }: React.PropsWithChildren<{}>) {
	const [attendeesPresenceInfo, setAttendeesPresenceInfo] = useState<AttendeesPresenceInfo>({});
	const [anonymousAttendees, setAnonymousAttendees] = useState<AnonymousAttendee[]>([]);

	const onUserPresenceChange = useCallback(
		(presence: UserPresence, addUserToAnonymous: boolean) => {
			const { sessionId, user, lastActive, isCurrentlyViewing } = presence;

			if (addUserToAnonymous) {
				let newAnonymousAttendees = [...anonymousAttendees];
				const isPresentInAnonymousList = anonymousAttendees.some(
					anonymousAttendee => anonymousAttendee.email === user.email,
				);
				if (!isCurrentlyViewing && isPresentInAnonymousList) {
					newAnonymousAttendees = anonymousAttendees.filter(
						anonymousAttendee => anonymousAttendee.email !== user.email,
					);
				} else if (isCurrentlyViewing && !isPresentInAnonymousList) {
					newAnonymousAttendees = anonymousAttendees.concat([
						{
							id: user.id,
							email: user.email,
							fullName: user.fullName,
							image: user.image,
						},
					]);
				}
				setAnonymousAttendees(newAnonymousAttendees);
			}

			setAttendeesPresenceInfo(prev => {
				const userSessions = prev[user.id];
				const newUserSessions = [
					{
						sessionId,
						isCurrentlyViewing: !!isCurrentlyViewing,
						// TODO: "Viewed x minutes ago"
						lastActive,
					},
					...(userSessions ?? []).filter(s => s.sessionId !== sessionId),
				];

				return {
					...prev,
					[user.id]: newUserSessions,
				};
			});
		},
		[anonymousAttendees],
	);
	return (
		<AttendeePresenceContext.Provider
			value={useMemo(
				() => ({ attendeesPresenceInfo, anonymousAttendees, onUserPresenceChange }),
				[anonymousAttendees, attendeesPresenceInfo, onUserPresenceChange],
			)}
		>
			{children}
		</AttendeePresenceContext.Provider>
	);
}

export function useAttendeePresenceContext() {
	return useContext(AttendeePresenceContext);
}
